import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Card,
    Grid,
    Typography,
    Box,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    Paper,
    Modal,
    Button,
    Radio,
    RadioGroup,
    FormControlLabel,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Divider,
    Tab,
    Tabs
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import QuizIcon from '@mui/icons-material/Quiz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import '../styles/PersonalLearningDetail.css';

const PersonalLearningDetail = () => {
    const { courseId } = useParams();
    const [topics, setTopics] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [aiOutput, setAiOutput] = useState(null);
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [open, setOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(0); // AI önerileri için tab kontrolü
    const [loading, setLoading] = useState({
        questions: true,
        topics: true,
        ai: true
    });
    const [courseContent, setCourseContent] = useState([]);
    const [completedContents, setCompletedContents] = useState([]);
    const userId = localStorage.getItem('userId');

    const getWrongQuestions = async (userId, courseId) => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/personal-learning/wrong-questions/${userId}/${courseId}`);
        if (!response.ok) throw new Error("Yanlış sorular alınamadı");
        return await response.json();
    };

    const getWeakTopics = async (userId, courseId) => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/personal-learning/weak-topics/${userId}/${courseId}`);
        if (!response.ok) throw new Error("Eksik konular alınamadı");
        return await response.json();
    };

    const generateAIStudyMaterial = async (userId, courseId) => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/personal-learning/generate-learning-material`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ studentId: userId, courseId: courseId })
        });
        if (!response.ok) throw new Error("AI çalışma materyali alınamadı");
        return await response.json();
    };

    const handleOpen = (question) => {
        setSelectedQuestion(question);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedQuestion(null);
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Progress hesaplama
    const total = courseContent.length;
    const completed = completedContents.length;
    const progress = total > 0 ? Math.round((completed / total) * 100) : 0;

    // AI Önerileri Render Fonksiyonları
    const renderTopicSection = () => {
        if (!aiOutput?.topics) return null;

        return (
            <Box>
                {aiOutput.topics.map((topic, index) => (
                    <Accordion
                        key={index}
                        sx={{
                            mb: 3,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                            border: '1px solid #f0e8de',
                            borderRadius: '12px !important',
                            overflow: 'hidden',
                            '&:before': { display: 'none' }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ color: '#8b5a3c' }} />}
                            sx={{
                                bgcolor: 'linear-gradient(135deg, #fef7ed 0%, #fdf4e8 100%)',
                                borderBottom: '1px solid #f0e8de',
                                py: 2
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                <LightbulbIcon sx={{ color: '#d4af8c', fontSize: 28 }} />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#8b5a3c' }}>
                                        {topic.name}
                                    </Typography>
                                    <Chip
                                        label={`${topic.keyPoints.length} önemli nokta`}
                                        size="small"
                                        sx={{
                                            mt: 0.5,
                                            bgcolor: '#f0e5d8',
                                            color: '#8b5a3c',
                                            fontWeight: 500
                                        }}
                                    />
                                </Box>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 3, bgcolor: '#fdfbf7' }}>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 600, color: '#8b5a3c', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircleIcon sx={{ color: '#a8d8a8' }} />
                                    Önemli Noktalar:
                                </Typography>
                                <List sx={{ pl: 0 }}>
                                    {topic.keyPoints.map((point, pointIndex) => (
                                        <ListItem key={pointIndex} sx={{
                                            py: 1,
                                            pl: 0,
                                            mb: 1,
                                            bgcolor: '#f8f6f2',
                                            borderRadius: 2,
                                            border: '1px solid #f0e8de'
                                        }}>
                                            <CheckCircleIcon sx={{ color: '#81c784', mr: 2, fontSize: 20 }} />
                                            <ListItemText
                                                primary={
                                                    <Typography sx={{ color: '#6b4e3d', lineHeight: 1.6 }}>
                                                        {point}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>

                            {topic.relatedQuestion && (
                                <Box sx={{
                                    mt: 4,
                                    p: 3,
                                    background: 'linear-gradient(135deg, #e3f2fd 0%, #f0f7ff 100%)',
                                    borderRadius: 3,
                                    border: '2px solid #bbdefb'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                        <QuizIcon sx={{ color: '#1976d2', mr: 2, fontSize: 24 }} />
                                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1565c0' }}>
                                            Pratik Sorusu
                                        </Typography>
                                    </Box>
                                    <Typography variant="body1" sx={{
                                        mb: 3,
                                        fontWeight: 500,
                                        color: '#1e3a5f',
                                        p: 2,
                                        bgcolor: 'rgba(255,255,255,0.6)',
                                        borderRadius: 2,
                                        border: '1px solid #e1f5fe'
                                    }}>
                                        {topic.relatedQuestion.question}
                                    </Typography>

                                    <RadioGroup>
                                        {topic.relatedQuestion.options.map((option, optIndex) => {
                                            const optionLetter = String.fromCharCode(65 + optIndex);
                                            const isCorrect = topic.relatedQuestion.correctAnswer === optionLetter;
                                            return (
                                                <FormControlLabel
                                                    key={optIndex}
                                                    value={option}
                                                    control={
                                                        <Radio
                                                            disabled
                                                            color={isCorrect ? "success" : "default"}
                                                            sx={{ color: isCorrect ? '#66bb6a' : '#bdbdbd' }}
                                                        />
                                                    }
                                                    label={`${optionLetter}) ${option}`}
                                                    sx={{
                                                        bgcolor: isCorrect ? 'linear-gradient(135deg, #e8f5e8 0%, #f0f9f0 100%)' : 'rgba(255,255,255,0.8)',
                                                        borderRadius: 2,
                                                        px: 2,
                                                        py: 1,
                                                        mb: 1,
                                                        border: isCorrect ? '2px solid #a8d8a8' : '1px solid #e0e0e0',
                                                        transition: 'all 0.3s ease',
                                                        '&:hover': {
                                                            transform: 'translateX(4px)'
                                                        }
                                                    }}
                                                />
                                            );
                                        })}
                                    </RadioGroup>

                                    <Box sx={{
                                        mt: 3,
                                        p: 3,
                                        background: 'linear-gradient(135deg, #e8f5e8 0%, #f0f9f0 100%)',
                                        borderRadius: 2,
                                        border: '2px solid #a8d8a8'
                                    }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#2e5d2e', lineHeight: 1.6 }}>
                                            <strong>💡 Açıklama:</strong> {topic.relatedQuestion.explanation}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>
        );
    };

    const renderExplanationsSection = () => {
        if (!aiOutput?.explanations) return null;

        return (
            <Box>
                {aiOutput.explanations.map((explanation, index) => (
                    <Card key={index} sx={{
                        mb: 4,
                        boxShadow: '0 6px 25px rgba(0,0,0,0.1)',
                        borderRadius: 3,
                        border: '1px solid #f0e8de',
                        overflow: 'hidden'
                    }}>
                        <Box sx={{
                            p: 3,
                            background: 'linear-gradient(135deg, #fef7ed 0%, #fff 100%)'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
                                <ErrorIcon sx={{ color: '#e57373', mt: 0.5, fontSize: 28 }} />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" sx={{ mb: 2, color: '#7d2d2d', fontWeight: 600 }}>
                                        🔍 Analiz Edilen Yanlış Soru
                                    </Typography>
                                    <Typography variant="body1" sx={{
                                        mb: 2,
                                        fontWeight: 500,
                                        color: '#6b4e3d',
                                        p: 2,
                                        bgcolor: '#fdfbf7',
                                        borderRadius: 2,
                                        border: '1px solid #f0e8de'
                                    }}>
                                        {explanation.originalQuestion}
                                    </Typography>
                                </Box>
                            </Box>

                            <Grid container spacing={3} sx={{ mb: 3 }}>
                                <Grid item xs={12} md={6}>
                                    <Box sx={{
                                        p: 3,
                                        background: 'linear-gradient(135deg, #fde8e8 0%, #fef0f0 100%)',
                                        borderRadius: 2,
                                        border: '2px solid #f5b2b2',
                                        height: '100%'
                                    }}>
                                        <Typography variant="subtitle2" sx={{ color: '#7d2d2d', mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            ❌ Sizin Cevabınız:
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#7d2d2d', fontWeight: 500 }}>
                                            {explanation.studentAnswer}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Box sx={{
                                        p: 3,
                                        background: 'linear-gradient(135deg, #e8f5e8 0%, #f0f9f0 100%)',
                                        borderRadius: 2,
                                        border: '2px solid #a8d8a8',
                                        height: '100%'
                                    }}>
                                        <Typography variant="subtitle2" sx={{ color: '#2e5d2e', mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            ✅ Doğru Cevap:
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#2e5d2e', fontWeight: 500 }}>
                                            {explanation.correctAnswer}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 3, borderColor: '#f0e8de' }} />

                            <Box sx={{ mb: 4 }}>
                                <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 600, color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    🤔 Neden Yanlış?
                                </Typography>
                                <Typography variant="body2" sx={{
                                    color: '#6b4e3d',
                                    mb: 3,
                                    p: 2,
                                    bgcolor: '#fdf6f0',
                                    borderRadius: 2,
                                    border: '1px solid #f0e8de',
                                    lineHeight: 1.6
                                }}>
                                    {explanation.whyWrong}
                                </Typography>

                                <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 600, color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    💡 Nasıl Çözülür?
                                </Typography>
                                <Typography variant="body2" sx={{
                                    color: '#6b4e3d',
                                    p: 2,
                                    bgcolor: '#f0f7ff',
                                    borderRadius: 2,
                                    border: '1px solid #bbdefb',
                                    lineHeight: 1.6
                                }}>
                                    {explanation.howToSolve}
                                </Typography>
                            </Box>

                            {explanation.similarNewQuestion && (
                                <Box sx={{
                                    mt: 4,
                                    p: 4,
                                    background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f7ff 100%)',
                                    borderRadius: 3,
                                    border: '2px solid #bbdefb'
                                }}>
                                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#1565c0', display: 'flex', alignItems: 'center', gap: 1 }}>
                                        🎯 Benzer Alıştırma Sorusu
                                    </Typography>
                                    <Typography variant="body1" sx={{
                                        mb: 3,
                                        fontWeight: 500,
                                        color: '#1e3a5f',
                                        p: 3,
                                        bgcolor: 'rgba(255,255,255,0.8)',
                                        borderRadius: 2,
                                        border: '1px solid #e1f5fe'
                                    }}>
                                        {explanation.similarNewQuestion.question}
                                    </Typography>

                                    <RadioGroup>
                                        {explanation.similarNewQuestion.options.map((option, optIndex) => {
                                            const optionLetter = String.fromCharCode(65 + optIndex);
                                            const isCorrect = explanation.similarNewQuestion.correctAnswer === optionLetter;
                                            return (
                                                <FormControlLabel
                                                    key={optIndex}
                                                    value={option}
                                                    control={
                                                        <Radio
                                                            disabled
                                                            color={isCorrect ? "success" : "default"}
                                                            sx={{ color: isCorrect ? '#66bb6a' : '#bdbdbd' }}
                                                        />
                                                    }
                                                    label={`${optionLetter}) ${option}`}
                                                    sx={{
                                                        bgcolor: isCorrect ? 'linear-gradient(135deg, #e8f5e8 0%, #f0f9f0 100%)' : 'rgba(255,255,255,0.8)',
                                                        borderRadius: 2,
                                                        px: 2,
                                                        py: 1,
                                                        mb: 1,
                                                        border: isCorrect ? '2px solid #a8d8a8' : '1px solid #e0e0e0',
                                                        transition: 'all 0.3s ease',
                                                        '&:hover': {
                                                            transform: 'translateX(4px)'
                                                        }
                                                    }}
                                                />
                                            );
                                        })}
                                    </RadioGroup>

                                    <Box sx={{
                                        mt: 3,
                                        p: 3,
                                        background: 'linear-gradient(135deg, #e8f5e8 0%, #f0f9f0 100%)',
                                        borderRadius: 2,
                                        border: '2px solid #a8d8a8'
                                    }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#2e5d2e', lineHeight: 1.6 }}>
                                            <strong>✅ Doğru Cevap Açıklaması:</strong> {explanation.similarNewQuestion.correctExplanation}
                                        </Typography>
                                    </Box>

                                    {/* Yanlış Şıkların Açıklamaları */}
                                    {explanation.similarNewQuestion.wrongExplanations && (
                                        <Box sx={{ mt: 3 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 600, color: '#7d2d2d', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                📚 Yanlış Şıkların Açıklamaları:
                                            </Typography>
                                            {Object.entries(explanation.similarNewQuestion.wrongExplanations).map(([option, wrongExpl], index) => (
                                                <Box key={index} sx={{
                                                    mb: 2,
                                                    p: 3,
                                                    background: 'linear-gradient(135deg, #fff3e0 0%, #fffaf0 100%)',
                                                    borderRadius: 2,
                                                    border: '2px solid #ffcc8a',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: '0 4px 15px rgba(255, 204, 138, 0.3)'
                                                    }
                                                }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#8b5a00', lineHeight: 1.6 }}>
                                                        <strong>❌ {option} Şıkkı:</strong> {wrongExpl}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Card>
                ))}
            </Box>
        );
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading({ questions: true, topics: true, ai: true });

                const contentsResponse = await fetch(`${process.env.REACT_APP_API_URL}/courseContents/getByCourseId/${courseId}`);
                const completedResponse = await fetch(`${process.env.REACT_APP_API_URL}/course-progress/completed`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        participantId: userId,
                        courseId: courseId
                    })
                });

                const [wrong, weakTopics, aiMaterial, contents, completed] = await Promise.all([
                    getWrongQuestions(userId, courseId),
                    getWeakTopics(userId, courseId),
                    generateAIStudyMaterial(userId, courseId),
                    contentsResponse.json(),
                    completedResponse.json()
                ]);

                const formattedQuestions = wrong.map(q => ({
                    questionId: q.question.id,
                    topic: q.question.topic,
                    questionText: q.question.question,
                    options: q.question.options,
                    correctAnswer: q.question.correctAnswer,
                    selectedOption: q.selectedOption,
                    difficulty: q.question.difficulty,
                    explanation: q.question.explanation
                }));

                setQuestions(formattedQuestions);
                setTopics(weakTopics);

                // AI önerilerini parse et
                let parsedAiOutput = null;
                if (aiMaterial) {
                    if (typeof aiMaterial.recommendations === 'string') {
                        try {
                            // JSON string içindeki markdown formatını temizle
                            const cleanJson = aiMaterial.recommendations.replace(/```json\n?|\n?```/g, '');
                            parsedAiOutput = JSON.parse(cleanJson);
                        } catch (parseError) {
                            console.error("AI JSON parse hatası:", parseError);
                            parsedAiOutput = null;
                        }
                    } else if (typeof aiMaterial.recommendations === 'object') {
                        parsedAiOutput = aiMaterial.recommendations;
                    }
                }

                setAiOutput(parsedAiOutput);
                setCourseContent(contents);
                setCompletedContents(completed);

                console.log("AI Output:", parsedAiOutput);

            } catch (e) {
                console.error(e);
            } finally {
                setLoading({ questions: false, topics: false, ai: false });
            }
        };

        fetchData();
    }, [courseId, userId]);

    return (
        <Box className="page-container">
            <Typography variant="h4" gutterBottom className="page-title">
                Mamografi Dersi
            </Typography>

            <Grid container spacing={3}>
                {/* İlerleme Düzeyi */}
                <Grid item xs={12} md={4}>
                    <Card className="progress-card">
                        <Typography variant="h6" gutterBottom className="section-title">
                            İlerleme Düzeyi
                        </Typography>
                        {loading.topics ? (
                            <CircularProgress className="progress-circle" />
                        ) : (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                p: 2
                            }}>
                                {/* Kupa ve Progress Kombinasyonu */}
                                <Box sx={{
                                    position: 'relative',
                                    mb: 3,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <CircularProgress
                                        variant="determinate"
                                        value={progress}
                                        size={140}
                                        thickness={4}
                                        sx={{
                                            color: progress >= 80 ? '#FFD700' : progress >= 60 ? '#C0C0C0' : progress >= 40 ? '#CD7F32' : '#8e8e93',
                                            '& .MuiCircularProgress-circle': {
                                                strokeLinecap: 'round',
                                            }
                                        }}
                                    />
                                    <Box sx={{
                                        position: 'absolute',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <EmojiEventsIcon
                                            sx={{
                                                fontSize: 60,
                                                color: progress >= 80 ? '#FFD700' : progress >= 60 ? '#C0C0C0' : progress >= 40 ? '#CD7F32' : '#8e8e93',
                                                mb: 1,
                                                filter: `drop-shadow(0 2px 4px rgba(${progress >= 80 ? '255, 215, 0' : progress >= 60 ? '192, 192, 192' : progress >= 40 ? '205, 127, 50' : '142, 142, 147'}, 0.3))`
                                            }}
                                        />
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 700,
                                                color: progress >= 80 ? '#B8860B' : progress >= 60 ? '#8b5a3c' : progress >= 40 ? '#8B4513' : '#666',
                                                fontSize: '1.1rem'
                                            }}
                                        >
                                            {progress}%
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Başarı Seviyesi Göstergesi */}
                                <Box sx={{ textAlign: 'center', mb: 2 }}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 600,
                                            color: progress >= 80 ? '#B8860B' : progress >= 60 ? '#8b5a3c' : progress >= 40 ? '#8B4513' : '#666',
                                            mb: 0.5
                                        }}
                                    >
                                        {progress >= 80 ? '🏆 Gold Seviye' :
                                         progress >= 60 ? '🥈 Silver Seviye' :
                                         progress >= 40 ? '🥉 Bronze Seviye' :
                                         '📚 Başlangıç Seviyesi'}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: '#666',
                                            fontWeight: 500
                                        }}
                                    >
                                        {completed} / {total} içerik tamamlandı
                                    </Typography>
                                </Box>

                                {/* İlerleme Çubuğu */}
                                <Box sx={{ width: '100%', mt: 2 }}>
                                    <Box sx={{
                                        width: '100%',
                                        height: 8,
                                        backgroundColor: '#f0f0f0',
                                        borderRadius: 4,
                                        overflow: 'hidden',
                                        position: 'relative'
                                    }}>
                                        <Box sx={{
                                            width: `${progress}%`,
                                            height: '100%',
                                            background: progress >= 80 ?
                                                'linear-gradient(90deg, #FFD700, #FFA500)' :
                                                progress >= 60 ?
                                                'linear-gradient(90deg, #C0C0C0, #A8A8A8)' :
                                                progress >= 40 ?
                                                'linear-gradient(90deg, #CD7F32, #B8860B)' :
                                                'linear-gradient(90deg, #8e8e93, #6d6d70)',
                                            borderRadius: 4,
                                            transition: 'width 0.3s ease'
                                        }} />
                                    </Box>
                                </Box>

                                {/* Motivasyon Mesajı */}
                                <Typography
                                    variant="caption"
                                    sx={{
                                        mt: 2,
                                        color: '#888',
                                        textAlign: 'center',
                                        fontStyle: 'italic'
                                    }}
                                >
                                    {progress >= 80 ? 'Mükemmel! Harika gidiyorsun! 🎉' :
                                     progress >= 60 ? 'Çok iyi! Devam et! 💪' :
                                     progress >= 40 ? 'İyi başlangıç! Daha da ilerle! 🚀' :
                                     'Başlangıç seviyesi, çalışmaya devam! 📖'}
                                </Typography>
                            </Box>
                        )}
                    </Card>
                </Grid>

                {/* Zayıf Konular */}
                <Grid item xs={12} md={8}>
                    <Card className="topics-card">
                        <Typography variant="h6" gutterBottom className="section-title">
                            Geliştirilmesi Gereken Konular
                        </Typography>
                        {loading.topics ? (
                            <Box className="loading-container">
                                <CircularProgress className="progress-circle" />
                            </Box>
                        ) : (
                            <List>
                                {topics.map((topic, index) => (
                                    <ListItem key={index} className="topic-item">
                                        <ListItemText
                                            primary={
                                                <Typography className="topic-text">
                                                    {topic.topic}
                                                </Typography>
                                            }
                                            secondary={
                                                <Typography className="topic-count">
                                                    Yanlış Sayısı: {topic.wrongCount}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Card>
                </Grid>

                {/* Yanlış Sorular */}
                <Grid item xs={12}>
                    <Card className="questions-card">
                        <Typography variant="h6" gutterBottom className="section-title">
                            Yanlış Yapılan Sorular
                        </Typography>
                        {loading.questions ? (
                            <Box className="loading-container">
                                <CircularProgress className="progress-circle" />
                            </Box>
                        ) : (
                            <Grid container spacing={3}>
                                {questions.map((question) => (
                                    <Grid item xs={12} sm={6} md={3} key={question.questionId}>
                                        <Paper
                                            className="question-paper"
                                            elevation={0}
                                            onClick={() => handleOpen(question)}
                                        >
                                            <Typography className="question-topic">
                                                {question.topic}
                                            </Typography>
                                            <Typography variant="body2" className="question-text">
                                                {question.questionText}
                                            </Typography>
                                            <Typography variant="caption" className="question-difficulty">
                                                Zorluk: {question.difficulty}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Card>
                </Grid>

                {/* AI Önerileri - Yeni Tasarım */}
                <Grid item xs={12}>
                    <Card className="ai-card" sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                        <Box sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom className="section-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LightbulbIcon sx={{ color: '#ff9800' }} />
                                AI Önerileri ve Detaylı Analiz
                            </Typography>

                            {loading.ai ? (
                                <Box className="loading-container">
                                    <CircularProgress className="progress-circle" />
                                </Box>
                            ) : aiOutput ? (
                                <Box>
                                    <Tabs
                                        value={activeTab}
                                        onChange={handleTabChange}
                                        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
                                    >
                                        <Tab
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <LightbulbIcon fontSize="small" />
                                                    Konu Önerileri
                                                    <Chip label={aiOutput.topics?.length || 0} size="small" />
                                                </Box>
                                            }
                                        />
                                        <Tab
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <ErrorIcon fontSize="small" />
                                                    Hata Analizi
                                                    <Chip label={aiOutput.explanations?.length || 0} size="small" />
                                                </Box>
                                            }
                                        />
                                    </Tabs>

                                    <Box>
                                        {activeTab === 0 && renderTopicSection()}
                                        {activeTab === 1 && renderExplanationsSection()}
                                    </Box>
                                </Box>
                            ) : (
                                <Typography variant="body1" className="question-text">
                                    Henüz AI önerisi bulunmuyor.
                                </Typography>
                            )}
                        </Box>
                    </Card>
                </Grid>
            </Grid>

            {/* Modal */}
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="question-detail-modal"
                className="question-modal"
            >
                <Box className="modal-content">
                    {selectedQuestion && (
                        <>
                            <Typography variant="h6" component="h2" className="modal-question-title">
                                {selectedQuestion.topic}
                            </Typography>
                            <Typography variant="body1" className="modal-question-text">
                                {selectedQuestion.questionText}
                            </Typography>

                            {selectedQuestion.options && (
                                <RadioGroup className="modal-radio-group">
                                    {selectedQuestion.options.map((option, index) => (
                                        <FormControlLabel
                                            key={index}
                                            value={option}
                                            control={
                                                <Radio
                                                    disabled
                                                    color={
                                                        option === selectedQuestion.correctAnswer
                                                            ? "success"
                                                            : option === selectedQuestion.selectedOption
                                                                ? "error"
                                                                : "default"
                                                    }
                                                />
                                            }
                                            label={option}
                                            className={`option-label ${
                                                option === selectedQuestion.correctAnswer
                                                    ? "correct"
                                                    : option === selectedQuestion.selectedOption
                                                        ? "incorrect"
                                                        : ""
                                            }`}
                                        />
                                    ))}
                                </RadioGroup>
                            )}

                            <Box className="modal-answer-box correct">
                                <Typography variant="subtitle1" className="modal-answer-text">
                                    Doğru Cevap: {selectedQuestion.correctAnswer}
                                </Typography>
                            </Box>

                            <Box className="modal-answer-box incorrect">
                                <Typography variant="subtitle1" className="modal-answer-text">
                                    Sizin Cevabınız: {selectedQuestion.selectedOption}
                                </Typography>
                            </Box>

                            {selectedQuestion.explanation && (
                                <Box className="modal-answer-box explanation">
                                    <Typography variant="subtitle1" className="modal-answer-text">
                                        Açıklama: {selectedQuestion.explanation}
                                    </Typography>
                                </Box>
                            )}

                            <Button
                                onClick={handleClose}
                                variant="contained"
                                className="modal-close-button"
                            >
                                Kapat
                            </Button>
                        </>
                    )}
                </Box>
            </Modal>
        </Box>
    );
};

export default PersonalLearningDetail;

